import { useTenantAvailable } from '@api/application-configuration';
import { useLogoutMutation } from '@api/auth';
import { LogOut } from '@tamagui/lucide-icons';
import { Accordion, AccordionProps } from '@unistocks/components/accordion/accordion';
import { Group, GroupProps } from '@unistocks/components/group/group';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { Heading } from '@unistocks/components/text/text';
import { useAdminRoutes } from '@unistocks/features/admin/navigation/use-admin-routes';
import { Route } from '@unistocks/features/navigation/route';
import { RouteListItem } from '@unistocks/features/navigation/route-list-item';
import { useRouteActive } from '@unistocks/features/navigation/use-route-active';
import { useAppRoutes, useSecondaryRoutes } from '@unistocks/features/navigation/use-routes';
import { t } from '@unistocks/shared/localization/index.ts';
import { Link, useGlobalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { ListItem, Popover, Separator, useMedia, View, YGroup } from 'tamagui';

type RoutesProps = {
  onPress?: (route: Route) => void;
};

export type RoutesNavBarProps = RoutesProps & Omit<GroupProps, 'onPress'>;

export type RoutesAccordionProps = RoutesProps &
  Omit<AccordionProps, 'type' | 'value' | 'onValueChange' | 'defaultValue'> & {
    collapsed?: boolean;
    withSignOut?: boolean;
  };

type RouterLinkProps = {
  route: Route;
  onPress?: (route: Route) => void;
};

type Item = { type: 'route'; route: Route } | { type: 'signout' };

export const RoutesNavBar: React.FC<RoutesNavBarProps> = ({ onPress, ...props }) => {
  const routes = useAppRoutes();

  return (
    <Group render="ul" gap="$5" p={0} {...props}>
      {routes.map(route => (
        <Group.Item key={route.key}>
          <RouterLink route={route} onPress={onPress} />
        </Group.Item>
      ))}
    </Group>
  );
};

RoutesNavBar.displayName = 'RoutesNavBar';

const RouterLink: React.FC<RouterLinkProps> = ({ route, onPress }) => {
  const isActive = useRouteActive(route);
  const { id } = useGlobalSearchParams();
  const hasChildren = !!route.children?.length;
  const disabled = !hasChildren;

  return (
    <Popover
      hoverable
      stayInFrame
      open={disabled ? false : undefined}
      placement="bottom-start"
      size={'$5'}
      offset={14}
    >
      <Popover.Trigger asChild>
        <View render="li" borderBottomWidth={isActive ? 2 : 0} borderColor={'$unistocks'}>
          <Link href={route.href} onPress={onPress ? () => onPress(route) : undefined}>
            <View fd="row" alignItems="center" gap="$2">
              <route.Icon
                color={isActive ? '$unistocks' : '$headingFont'}
                strokeWidth={isActive ? '$3' : undefined}
              />
              <Heading size={'$6'} fontWeight={'$5'}>
                {route.name}
              </Heading>
            </View>
          </Link>
        </View>
      </Popover.Trigger>

      <Popover.Content
        elevation={'$1'}
        borderWidth={1}
        p={0}
        borderColor="$borderColor"
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        transition={'quickLessBouncy'}
      >
        <Popover.Arrow borderWidth={1} />
        <YGroup render="ul">
          {route.children?.map((child, i, arr) => (
            <React.Fragment key={child.key}>
              {i > 0 && <Separator />}
              <YGroup.Item>
                <Link
                  asChild
                  href={child.href}
                  onPress={onPress ? () => onPress(child) : undefined}
                >
                  <ListItem
                    active={
                      isActive && typeof child.href !== 'string' && child.href.params?.id === id
                    }
                    size={'$5'}
                    gap={'$3'}
                    icon={child.Icon}
                    title={child.name}
                  />
                </Link>
              </YGroup.Item>
            </React.Fragment>
          ))}
        </YGroup>
      </Popover.Content>
    </Popover>
  );
};

RouterLink.displayName = 'RouterLink';

export const RoutesAccordion: React.FC<RoutesAccordionProps> = ({
  onPress,
  collapsed,
  withSignOut,
  ...props
}) => {
  const isTenantAvailable = useTenantAvailable();
  const media = useMedia();
  const appRoutes = useAppRoutes();
  const adminRoutes = useAdminRoutes();
  const secondaryRoutes = useSecondaryRoutes();
  const [value, onValueChange] = useState<string>();
  const { mutate: logout, isPending } = useLogoutMutation();

  const items: Item[] = [
    ...(isTenantAvailable ? appRoutes : adminRoutes).map(
      route => ({ type: 'route', route }) as Item
    ),
    ...(!media.md && !isTenantAvailable
      ? secondaryRoutes.map(route => ({ type: 'route', route }) as Item)
      : []),
    ...(withSignOut ? [{ type: 'signout' } as Item] : [])
  ];

  return (
    <Accordion
      type={'single'}
      collapsible
      value={collapsed ? '' : value}
      onValueChange={onValueChange}
      {...props}
    >
      <Group separator={<Separator />}>
        {items.map(item =>
          item.type === 'route' ? (
            <YGroup.Item key={item.route.key}>
              <RouteAccordionItem
                route={item.route}
                collapsed={collapsed}
                onPress={onPress}
                onValueChange={onValueChange as React.Dispatch<React.SetStateAction<string>>}
              />
            </YGroup.Item>
          ) : (
            <ListItem
              key={'signout'}
              disabled={isPending}
              size={'$6'}
              gap={'$3'}
              icon={LogOut}
              iconAfter={isPending ? Spinner : undefined}
              title={t('::Logout')}
              onPress={() => logout()}
            />
          )
        )}
      </Group>
    </Accordion>
  );
};

RoutesAccordion.displayName = 'RoutesAccordion';

type RoutesAccordionItemProps = {
  route: Route;
  collapsed?: boolean;
  onPress?: (route: Route) => void;
  onValueChange: React.Dispatch<React.SetStateAction<string>>;
};

const RouteAccordionItem: React.FC<RoutesAccordionItemProps> = ({
  route,
  onPress,
  onValueChange,
  collapsed
}) => {
  const hasChildren = !!route.children?.length;

  return (
    <Accordion.Item key={route.key} value={route.key}>
      <Accordion.Trigger disabled={route.children?.length === 0}>
        {({ open }: { open: boolean }) => {
          return hasChildren ? (
            <RouteListItem
              route={route}
              open={open}
              collapsed={collapsed}
              size={'$5'}
              p={'$3'}
              scaleIcon={1.5}
              onMouseEnter={() => onValueChange(route.key)}
            />
          ) : (
            <Link href={route.href}>
              <RouteListItem
                route={route}
                open={open}
                collapsed={collapsed}
                size={'$5'}
                p={'$3'}
                scaleIcon={1.5}
                onPress={onPress ? () => onPress(route) : undefined}
              />
            </Link>
          );
        }}
      </Accordion.Trigger>
      <Accordion.Content>
        {route.children?.map((child, i) => {
          return (
            <React.Fragment key={child.key}>
              {i > 0 && <Separator />}
              <Link href={child.href}>
                <RouteListItem
                  route={child}
                  collapsed={collapsed}
                  size={'$4'}
                  p={'$3'}
                  pl={'$5'}
                  scaleIcon={1.5}
                  onPress={() => onPress?.(child)}
                />
              </Link>
            </React.Fragment>
          );
        })}
      </Accordion.Content>
    </Accordion.Item>
  );
};

RouteAccordionItem.displayName = 'RouteAccordionItem';
